import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSesion } from '../sesion/Sesion';
import { Boton, clases } from '../comun/componentes';

/**
 * Los ocho ítems que fija el encargo. Todo lo demás vive dentro de ellos: la navegación no
 * crece cuando crece el sistema, y por eso una recepcionista nueva la aprende en un día.
 */
export const MODULOS = [
  { a: '/', rotulo: 'Inicio', icono: '◧', exacto: true },
  { a: '/pacientes', rotulo: 'Pacientes', icono: '☺' },
  { a: '/agenda', rotulo: 'Agenda', icono: '▦' },
  { a: '/tratamientos', rotulo: 'Tratamientos', icono: '⚕' },
  { a: '/finanzas', rotulo: 'Finanzas', icono: '₲' },
  { a: '/clinica', rotulo: 'Clínica', icono: '⚗' },
  { a: '/seguimientos', rotulo: 'Seguimientos', icono: '✆' },
  { a: '/administracion', rotulo: 'Administración', icono: '⚙' },
] as const;

export function Marco() {
  const { usuario, salir, cambiarDeClinica } = useSesion();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const ubicacion = useLocation();

  // Cambiar de pantalla cierra el menú de teléfono: si queda abierto tapa el contenido.
  useEffect(() => setMenuAbierto(false), [ubicacion.pathname]);

  return (
    <div className="min-h-dvh bg-papel-100">
      <a href="#contenido" className="salto-contenido">
        Saltar al contenido
      </a>

      <header className="sticky top-0 z-30 border-b border-papel-300 bg-papel-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-3 py-2 tablet:px-5">
          <button
            type="button"
            onClick={() => setMenuAbierto((abierto) => !abierto)}
            aria-expanded={menuAbierto}
            aria-controls="navegacion-principal"
            className="flex h-tactil w-tactil items-center justify-center rounded-lg text-xl text-tinta-600 hover:bg-papel-200 portatil:hidden"
          >
            <span aria-hidden="true">{menuAbierto ? '✕' : '☰'}</span>
            <span className="sr-only">Menú</span>
          </button>

          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="grid h-8 w-8 place-items-center rounded-lg bg-marca-600 text-sm font-bold text-white"
            >
              DC
            </span>
            <span className="text-base font-semibold tracking-tight text-tinta-900">
              AdvanceOdonto
            </span>
          </span>

          <div className="ml-auto flex items-center gap-2">
            {usuario && usuario.clinicas.length > 1 && (
              <label className="hidden items-center gap-2 tablet:flex">
                <span className="sr-only">Clínica activa</span>
                <select
                  value={usuario.clinicaId}
                  onChange={(e) => void cambiarDeClinica(e.target.value)}
                  className="h-tactil rounded-lg border border-papel-300 bg-white px-3 text-sm text-tinta-700"
                >
                  {usuario.clinicas.map((clinica) => (
                    <option key={clinica.id} value={clinica.id}>
                      {clinica.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="hidden text-right leading-tight tablet:block">
              <p className="text-dato font-medium text-tinta-800">{usuario?.nombreCompleto}</p>
              <p className="text-dato text-tinta-400">{usuario?.rol}</p>
            </div>

            <Boton variante="silencioso" onClick={() => void salir()}>
              Salir
            </Boton>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px] gap-0 portatil:gap-6 portatil:px-5">
        <nav
          id="navegacion-principal"
          aria-label="Navegación principal"
          className={clases(
            'shrink-0 border-b border-papel-300 bg-papel-50 portatil:sticky portatil:top-[57px]',
            'portatil:h-[calc(100dvh-57px)] portatil:w-56 portatil:border-b-0 portatil:bg-transparent portatil:py-5',
            menuAbierto ? 'block' : 'hidden portatil:block',
          )}
        >
          <ul className="flex flex-col gap-0.5 p-2 portatil:p-0">
            {MODULOS.map((modulo) => (
              <li key={modulo.a}>
                <NavLink
                  to={modulo.a}
                  end={'exacto' in modulo ? modulo.exacto : false}
                  className={({ isActive }) =>
                    clases(
                      'flex min-h-tactil items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-marca-600 text-white'
                        : 'text-tinta-600 hover:bg-papel-200 hover:text-tinta-900',
                    )
                  }
                >
                  <span aria-hidden="true" className="w-5 text-center text-base">
                    {modulo.icono}
                  </span>
                  {modulo.rotulo}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main id="contenido" className="min-w-0 flex-1 px-3 py-4 tablet:px-5 portatil:px-0 portatil:py-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Encabezado de pantalla: título, contexto y una sola acción primaria. */
export function Encabezado({
  titulo,
  detalle,
  accion,
}: {
  titulo: string;
  detalle?: string;
  accion?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-tinta-900 tablet:text-2xl">
          {titulo}
        </h1>
        {detalle && <p className="mt-1 text-sm text-tinta-500">{detalle}</p>}
      </div>
      {accion}
    </div>
  );
}

/** Pestañas dentro de un módulo: es el «progressive disclosure» del encargo. */
export function Pestanas({
  actual,
  opciones,
  alCambiar,
}: {
  actual: string;
  opciones: { valor: string; rotulo: string; contador?: number }[];
  alCambiar: (valor: string) => void;
}) {
  return (
    <div role="tablist" className="mb-4 flex gap-1 overflow-x-auto border-b border-papel-300">
      {opciones.map((opcion) => (
        <button
          key={opcion.valor}
          role="tab"
          type="button"
          aria-selected={actual === opcion.valor}
          onClick={() => alCambiar(opcion.valor)}
          className={clases(
            'flex min-h-tactil shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors',
            actual === opcion.valor
              ? 'border-marca-600 text-marca-700'
              : 'border-transparent text-tinta-500 hover:text-tinta-800',
          )}
        >
          {opcion.rotulo}
          {opcion.contador != null && opcion.contador > 0 && (
            <span className="rounded-full bg-papel-200 px-1.5 text-dato text-tinta-600">
              {opcion.contador}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
